/**
 * Eine Aufnahme, eine Stimme — über alle Seiten desselben Browsers.
 * (01.10.2026, Ticket 2882)
 *
 * ## Der Vorfall (01.10., 08:46–08:48, Martin am Mac)
 *
 * Eine Frage Vespera, das Mikro ging von selbst auf (`via=nach-frage`), Martin
 * sprach — und klickte 7 s später auf die Sprechtaste. Zwei Aufnahmen
 * liefen gleichzeitig am selben Mikrofon, zwei Uploads im Abstand von 4 s, zwei
 * fast wortgleiche Texte, zwei volle Züge, zwei Stimmen.
 *
 * Auf EINER Seite wäre das nicht möglich gewesen: dort steht der Druck bei
 * laufender Aufnahme als „Senden" (onPttPointerDown) und `startPttRecording`
 * hat seinen Riegel. Die Messzeile des zweiten Starts nennt den Grund:
 * `lastTtsEnd=nie` — diese Seite hatte nie ein Vorlesen beendet —, und
 * `kalt=nicht-gewaermt:noch-nicht-gesprochen` — sie hatte nie aufgenommen.
 * Die erste Aufnahme trug `lastTtsEnd=-639ms`. Es waren ZWEI Seiten
 * desselben Kontos, beide offen: jede kennt nur ihren eigenen Zustand
 * (`pttState`, `pttRecorder`, ihre Sprech-Schlangen) — ZWEI BESITZER.
 *
 * ## Was hier steht
 *
 * Ein winziger Kanal zwischen den Seiten desselben Browsers (BroadcastChannel,
 * gleiche Herkunft). Keine Anmeldung, kein Server, kein Inhalt: nur „ich nehme
 * auf (Weg, seit wann)" und „ich beginne zu sprechen (wann)".
 *
 *  - AUFNAHME: wer anfängt, belegt. Jede andere Seite weist einen Start ab,
 *    solange eine Seite aufnimmt (`fremdeAufnahme`). Beansprucht wird im ERSTEN
 *    Schritt eines Starts, nicht erst, wenn das Mikrofon da ist. Beanspruchen
 *    zwei Seiten im selben Augenblick, gewinnt die ältere Anmeldung
 *    (`gewinntFremdeAufnahme`), die jüngere weicht.
 *  - STIMME: wer zuletzt zu sprechen beginnt, spricht. Eine neue Antwort löst
 *    die laufende ab — auf dieser Seite wie auf jeder anderen (die ältere
 *    endet mit einem Abbruch, `tts-audio-abbruch`, nicht mit „Ende"). Dazu
 *    schweigen die eigenen Stimmen, sobald eine andere Seite eine Aufnahme
 *    beginnt (`nimmAn`): sonst spräche diese Seite in das Mikrofon der anderen.
 *  - SCHLANGE (16.10.2026): zwischen VERSCHIEDENEN Sprechern (Vespera ↔ Schmied) wartet die zweite Stimme, bis die erste
 *    zu Ende ist (`stimmeDarfBeginnen`) — „eine laufende Spur darf nicht unterbrochen werden“.
 *  - VORN (16.10.2026): eine Stimme beginnt nur auf dem Gerät, das gerade benutzt wird (Rückfall: sprechen).
 *  - AUFNAHME ÜBER GERÄTE (17.10.2026): nimmt ein anderes Gerät des Kontos auf, starten Wach-Wort und Auto-Mikro hier nicht
 *    (`zweitesGeraetSperrt`, über den Server — unten „Aufnahme über Geräte“).
 *  - BESITZ (28.10.2026): das Gerät, das die Wiedergabe begann, behält sie bis zum Ende seiner Reihe; ein anderes springt
 *    nicht ein, außer es ist weg oder Martins Hand ist dort (unten „Besitz“, shared/stimme-konto.ts). Nimmt ein anderes Gerät
 *    auf oder wurde es zuletzt echt bedient, beginnt der Besitzer keinen NEUEN Beitrag (Prüfbefund M2, `handAmAnderenGeraet`).
 *
 * ⚠️ Der Kanal gilt nur für Seiten DESSELBEN Browsers. Ein anderer Browser, die App
 *    auf dem Telefon oder ein Fenster mit eigenem Profil sehen ihn nicht. Für sie:
 *     - AUFNAHME: der Server fängt doppelte Aufnahmen ab (server/ptt-dublette.ts).
 *     - STIMME (seit 05.10.2026): dieselbe Regel über den Server — siehe unten
 *       „Konto" und shared/stimme-konto.ts.
 * ⚠️ Ohne BroadcastChannel (alte Umgebung, Tests) gibt es zwischen den Seiten
 *    eines Browsers nur die Abstimmung über den Server.
 */
import { GERAET_KOPF } from "../../shared/geraet-kennung.js";
import {
  AUFNAHME_BEWUSST,
  AUFNAHME_KONTO_GILT_MS,
  AUFNAHME_KONTO_TAKT_MS,
  BESITZ_GILT_MS,
  BESITZ_TAKT_MS,
  STIMME_AUFNAHME_WEG,
  STIMME_BEGINN_WEG,
  STIMME_BESITZ_WEG,
  STIMME_FERTIG_WEG,
  STIMME_VORN_WEG,
  besitzFremdGewinnt,
  gewinntFremdeKontoStimme,
  vornGrundEcht,
  type AufnahmeEreignis,
  type BesitzEreignis,
  type FertigEreignis,
  type StimmeEreignis,
  type VornEreignis,
} from "../../shared/stimme-konto.js";
import { EIGENES_GERAET } from "./geraet-kennung.js";

const KANAL = "voice-v8:seiten";
/** So oft erneuert eine aufnehmende Seite ihre Anmeldung. */
export const TAKT_MS = 1000;
/** So lange gilt eine Anmeldung ohne Erneuerung — drei Takte, dann ist die Seite weg. */
export const GILT_MS = 3500;
/**
 * So jung muss die eigene Aufnahme sein, um einer gleichzeitigen fremden zu
 * weichen. Darüber hat sie schon etwas gehört — und die andere Seite hätte
 * sie gesehen und gar nicht erst begonnen.
 */
export const RENNEN_MS = 2000;
/** Eine Messzeile je Sekunde, nicht mehr (siehe `doppelstartZeile`). */
export const MELDE_ABSTAND_MS = 1000;

type Nachricht =
  | { t: "aufnahme"; seite: string; via: string; seit: number; laeuft: boolean }
  | { t: "stimme"; seite: string; ts: number; kennung?: string }
  | { t: "fertig"; seite: string };

interface FremdeAufnahme {
  seite: string;
  via: string;
  /** Wie lange sie schon läuft (ms). */
  seitMs: number;
}

/** Wer ist zuerst da gewesen? Gleiche Zeit → die kleinere Kennung. */
export function gewinntFremdeAufnahme(
  eigen: { seit: number; seite: string },
  fremd: { seit: number; seite: string },
): boolean {
  if (fremd.seit !== eigen.seit) return fremd.seit < eigen.seit;
  return fremd.seite < eigen.seite;
}

/** Hat die fremde Stimme Vorrang? Die später begonnene gewinnt; gleiche Zeit → die größere Kennung. Derselbe Beitrag (`gleicherBeitrag`): die früher begonnene. */
export function gewinntFremdeStimme(
  eigen: { ts: number; seite: string },
  fremd: { ts: number; seite: string },
  gleicherBeitrag = false,
): boolean {
  // 26.10.2026: derselbe Beitrag — die früher begonnene Stimme bleibt (nicht die spätere).
  if (gleicherBeitrag) {
    if (fremd.ts !== eigen.ts) return fremd.ts < eigen.ts;
    return fremd.seite < eigen.seite;
  }
  if (fremd.ts !== eigen.ts) return fremd.ts > eigen.ts;
  return fremd.seite > eigen.seite;
}

/**
 * Die Messzeile für einen abgewiesenen Doppelstart — oder `null`, wenn in der
 * letzten Sekunde schon eine kam. Ruhig: ein Mensch, der mehrfach klickt,
 * füllt das Protokoll nicht.
 *
 * `laeuft`: `aufnahme` (läuft hier), `start` (der Start wartet noch aufs Mikro),
 * `andere-seite`.
 */
let letzteMeldung = -Infinity;
export function doppelstartZeile(
  via: string,
  laeuft: "aufnahme" | "start" | "andere-seite",
  jetzt: number,
  zusatz = "",
): string | null {
  if (jetzt - letzteMeldung < MELDE_ABSTAND_MS) return null;
  letzteMeldung = jetzt;
  return `[ptt-t] doppelstart abgewiesen via=${via} läuft=${laeuft}${zusatz ? " " + zusatz : ""}`;
}

// ── Kanal ────────────────────────────────────────────────────────────────

let kanal: BroadcastChannel | null | undefined;

function holeKanal(): BroadcastChannel | null {
  if (kanal !== undefined) return kanal;
  kanal = null;
  try {
    if (typeof BroadcastChannel === "undefined") return null;
    const k = new BroadcastChannel(KANAL);
    k.onmessage = (ev: MessageEvent): void => {
      try {
        nimmAn(ev.data as Nachricht);
      } catch {
        /* eine fremde Nachricht darf diese Seite nie stören */
      }
    };
    // Node (Tests): ein offener Kanal hielte sonst den Prozess am Leben.
    (k as unknown as { unref?: () => void }).unref?.();
    kanal = k;
  } catch {
    kanal = null;
  }
  return kanal;
}

/*
 * ⚠️ Eine Seite, die geschlossen oder neu geladen wird, meldet ihre Aufnahme
 *    ab. Sonst hielte ihr Schatten die Sperre noch drei Takte — und ein Druck
 *    gleich nach dem Neuladen würde von der eigenen, schon toten Seite
 *    abgewiesen.
 */
try {
  window.addEventListener("pagehide", () => {
    const e = eigeneAufnahme;
    if (e) sende({ t: "aufnahme", seite: EIGENES_GERAET, via: e.via, seit: e.seit, laeuft: false });
    kontoAufnahmeEnde();
  });
} catch {
  /* ohne window (Tests in node) gibt es nichts abzumelden */
}

/*
 * ⚠️ Der Kanal öffnet beim LADEN, nicht erst beim ersten Gebrauch. Eine Seite,
 *    die erst im Augenblick des Klicks zuhört, hört die laufende Anmeldung der
 *    anderen nicht — die kam vor ihrem Öffnen und wird erst im nächsten Takt
 *    erneuert. Genau in dieser Sekunde läge der Doppelstart.
 */
holeKanal();

function sende(n: Nachricht): void {
  try {
    holeKanal()?.postMessage(n);
  } catch {
    /* Kanal zu — dann gibt es eben nur die Sperre dieser Seite */
  }
}

/** Nur für Tests: den Kanal schließen und alles vergessen. */
export function _schliesse(): void {
  try {
    kanal?.close();
  } catch {
    /* egal */
  }
  kanal = undefined;
  if (takt !== null) clearInterval(takt);
  takt = null;
  eigeneAufnahme = null;
  fremdeAufnahmen.clear();
  verloren = null;
  stimmen.clear();
  meineStimmeTs = 0;
  hoechsteFremdeStimme = 0;
  kanalSeiten.clear();
  kontoLauf++;
  kontoOffen = false;
  kontoAbbruch = null;
  meineKontoNr = null;
  eigeneNrAusKanal = 0;
  fremdeSeitBeginn.clear();
  for (const w of [...wartende.values()]) w.ende("abbruch");
  vornFremd = null;
  ichVorn = false;
  eigeneAktivSeit = -Infinity;
  letzteVornMeldung = -Infinity;
  vornAus = false;
  if (vornTakt !== null) clearInterval(vornTakt);
  vornTakt = null;
  gesprocheneKennungen.clear();
  fremdGesprochen.clear();
  meineKennung = undefined;
  for (const p of [...geparkte]) p.ende("abbruch");
  kontoAufnahmeAus = false;
  kontoAufnahmeGemeldet = null;
  kontoAufnahmeLauf = 0;
  fremdeGeraeteAufnahmen.clear();
  fertigAnderswo = null;
  besitzAbgeben(false);
  besitzFremd = null;
  besitzAus = false;
  reihenfolgeAus = false;
  meinVorrang = false;
  letzteBehauptung = -Infinity;
  reiheOffenQuelle = () => false;
}

// ── Aufnahme ─────────────────────────────────────────────────────────────

let aufnahmeQuelle: () => boolean = () => false;
let eigeneAufnahme: { via: string; seit: number } | null = null;
const fremdeAufnahmen = new Map<string, { via: string; seit: number; bis: number }>();
let takt: ReturnType<typeof setInterval> | null = null;
let verloren: ((fremd: FremdeAufnahme) => void) | null = null;

/**
 * Wie es auf DIESER Seite wirklich um die Aufnahme steht — die harte
 * Wahrheit (läuft der Rekorder oder ein Start), nicht ein Zustandswort.
 * Der Takt fragt sie; steht sie auf „nein", gibt die Seite ihre Anmeldung
 * frei. Damit gibt es EIN Ende für jeden Ausgang (Senden, Abbruch, Absturz
 * des Starts) — kein Weg muss sich ums Freigeben kümmern.
 */
export function setzeAufnahmeQuelle(laeuft: () => boolean): void {
  aufnahmeQuelle = laeuft;
}

/** Läuft eine Aufnahme auf einer ANDEREN Seite? */
export function fremdeAufnahme(jetzt = Date.now()): FremdeAufnahme | null {
  holeKanal();
  for (const [seite, a] of fremdeAufnahmen) {
    if (a.bis <= jetzt) {
      fremdeAufnahmen.delete(seite);
      continue;
    }
    return { seite, via: a.via, seitMs: Math.max(0, jetzt - a.seit) };
  }
  return null;
}

/**
 * Der ERSTE Schritt eines Starts: ab jetzt gehört die Aufnahme dieser Seite.
 * Meldet es den anderen sofort und erneuert es im Takt, solange die Quelle
 * „läuft" sagt.
 */
export function aufnahmeBeanspruchen(via: string, jetzt = Date.now()): void {
  eigeneAufnahme = { via, seit: jetzt };
  sende({ t: "aufnahme", seite: EIGENES_GERAET, via, seit: jetzt, laeuft: true });
  // 16.10.2026: wer aufnimmt (auch das Wach-Wort im Hintergrund), benutzt dieses Gerät — es ist vorn. Das Mikro, das nach
  // einer Frage/Antwort VON SELBST aufgeht, ist keine Bedienung: es belegt nur ein freies „vorn“ (shared/stimme-konto.ts „HAFTEN“).
  geraetAktiv(via === "nach-frage" || via === "nach-antwort" ? "auto-aufnahme" : "aufnahme", jetzt);
  // 17.10.2026: auch die anderen GERÄTE sollen es wissen (Wach-Wort und Auto-Mikro bleiben dort zu) — unten „Aufnahme über Geräte“.
  kontoAufnahmeMelden(true, via, ++kontoAufnahmeLauf, jetzt);
  takt ??= setInterval(taktSchlag, TAKT_MS);
}

/**
 * Die harte Wahrheit ist gekippt — jetzt freigeben, nicht erst im nächsten Takt.
 *
 * Ohne diesen Aufruf bliebe die Anmeldung bis zu einer Sekunde stehen, nachdem
 * die Aufnahme schon gesendet oder verworfen ist: ein Klick auf einer anderen
 * Seite in dieser Zeit zeigte fälschlich „läuft schon in einem anderen
 * Fenster". Der Takt bleibt als Netz (Absturz eines Weges, der es vergisst).
 *
 * ⚠️ Fragt dieselbe Quelle wie der Takt: ein Aufruf, solange noch ein Start
 *    wartet oder der Rekorder läuft, tut NICHTS — er darf eine Aufnahme nie
 *    zu früh freigeben, auch wenn ein Weg ihn zur Unzeit ruft.
 */
export function aufnahmeFreigeben(): void {
  if (!eigeneAufnahme || aufnahmeQuelle()) return;
  taktSchlag();
}

function taktSchlag(): void {
  const e = eigeneAufnahme;
  if (!e || !aufnahmeQuelle()) {
    if (e) sende({ t: "aufnahme", seite: EIGENES_GERAET, via: e.via, seit: e.seit, laeuft: false });
    eigeneAufnahme = null;
    kontoAufnahmeEnde();
    if (takt !== null) clearInterval(takt);
    takt = null;
    return;
  }
  sende({ t: "aufnahme", seite: EIGENES_GERAET, via: e.via, seit: e.seit, laeuft: true });
  kontoAufnahmeErneuern(e.via);
}

/**
 * Eine andere Seite hat im selben Augenblick beansprucht und war zuerst da:
 * diese Seite weicht — der Rückruf wirft ihre (noch junge) Aufnahme weg.
 */
export function beiRennenVerloren(rueckruf: ((fremd: FremdeAufnahme) => void) | null): void {
  verloren = rueckruf;
}

// ── Stimme ───────────────────────────────────────────────────────────────

/** Was die Abstimmung von einer Sprech-Schlange braucht (TtsQueue erfüllt es). */
interface StimmenTeilnehmer {
  cancel(grund?: string): void;
  /** Wer spricht: Vespera (ohne Angabe) oder der Schmied. Nur zwischen VERSCHIEDENEN Sprechern wird gewartet. */
  readonly sprecher?: "schmied" | "probe" | undefined;
  /** Klingt oder lädt gerade Ton? Fehlt es, gilt die Stimme als klingend (die Frist läuft dann nicht). */
  isAudioActive?(): boolean;
  /** 17.10.2026: die Kennung des Beitrags, den diese Stimme spricht (nur der Schmied) — siehe „Geparkt“ unten. */
  readonly kennung?: string | undefined;
  /** 28.10.2026: Martins Abspielknopf (TtsQueue `ausdruecklich`) — hat Vorrang vor der Reihe eines anderen Geräts („Besitz“). */
  readonly ausdruecklich?: boolean | undefined;
}

const stimmen = new Set<StimmenTeilnehmer>();
let meineStimmeTs = 0;
/**
 * Der späteste Beginn einer fremden Stimme, den diese Seite je gehört hat.
 * Wer danach beginnt, beginnt NACH ihr — auch im selben Millisekundenschritt
 * der Uhr: sonst hielte jede Seite die andere für die frühere, und beide
 * sprächen weiter.
 */
let hoechsteFremdeStimme = 0;
/** Die Beitrags-Kennung der jüngsten eigenen Stimme (26.10.2026: gleicher Beitrag auf zwei Geräten — die frühere gewinnt). */
let meineKennung: string | undefined;

/**
 * Diese Schlange beginnt zu sprechen. Jede andere, die gerade spricht — hier
 * oder auf einer anderen Seite —, endet mit einem Abbruch (`neue-antwort`,
 * `andere-seite`). Mehrfach gerufen (jedes Stück der Antwort) tut es nur
 * beim ersten Mal etwas.
 *
 * ⚠️ BEWUSSTE WAHL: es gilt „wer zuletzt zu sprechen BEGINNT, spricht" — nicht
 *    „die NEUESTE Antwort spricht". Wird eine ältere Antwort langsam vertont und
 *    beginnt erst NACH der neueren, schneidet sie die neuere mitten im Satz ab
 *    (Nachprüfung 01.10., Befund S5). Das ist gewollt: nach dem Beginn einer
 *    Stimme weiß diese Seite nichts über die Reihenfolge der Züge (jede Seite
 *    kennt nur ihre eigenen), und ein Vorrang nach Zug-Nummer bräuchte eine
 *    Nummer je ZUG, die alle Seiten und Geräte teilen. Die Abstimmung über den
 *    Server (unten, „Konto") zählt Beginne, nicht Züge — dieselbe Regel, nur
 *    weiter gereicht: eine Stimme, und zwar die, die zuletzt anfing.
 */
export function stimmeBeginnt(t: StimmenTeilnehmer, jetzt = Date.now(), grund = "neue-antwort"): void {
  if (stimmen.has(t)) return;
  const abgeloest = [...stimmen];
  // ⚠️ ERST eintragen, dann ablösen: das Ende der alten ließe sonst eine wartende Stimme nachrücken (`wartendePruefen`),
  //    die diese neue gleich wieder abschnitte.
  stimmen.add(t);
  for (const andere of abgeloest) {
    try {
      andere.cancel(grund);
    } catch {
      /* eine Schlange darf die andere nicht aufhalten */
    }
    stimmen.delete(andere);
  }
  meineStimmeTs = Math.max(jetzt, hoechsteFremdeStimme + 1);
  // 17.10.2026 („Geparkt“ unten): WAS beginnt, reist mit — Seiten, die denselben Beitrag zurückhalten, verwerfen ihn.
  const kennung = t.kennung ? t.kennung : undefined;
  meineKennung = kennung;
  if (kennung) merkeGesprochen(kennung, jetzt);
  sende({ t: "stimme", seite: EIGENES_GERAET, ts: meineStimmeTs, ...(kennung ? { kennung } : {}) });
  // 28.10.2026 („Besitz“ unten): diese Seite beginnt — ihre Reihe gehört ihr bis zum Ende. Ein Entwickler-Beitrag, der von
  // selbst beginnt, reist als `nachrang` mit (er löst keine fremde Reihe ab).
  meinVorrang = stimmeHatVorrang(t);
  besitzUebernehmen(jetzt);
  kontoBeginn(kennung, reihenfolgeAn() && !meinVorrang);
}

/**
 * Alle Stimmen DIESER Seite enden mit einem Abbruch — eine Schlange darf die andere nicht aufhalten. Auch die WARTENDEN
 * (unten, „Schlange“): eine andere Seite nimmt auf oder ein anderes Gerät spricht — dann soll hier nichts nachrücken.
 */
function stimmenBeenden(grund: string): void {
  // ⚠️ Die WARTENDEN zuerst: endet erst die sprechende, rückte die wartende nach (`stimmeEndet` → `wartendePruefen`) und
  //    begänne zu sprechen — genau in die Aufnahme der anderen Seite hinein (Test „auch die WARTENDE Stimme endet“).
  for (const w of [...wartende.values()]) {
    try {
      w.t.cancel(grund);
    } catch {
      /* eine Schlange darf die andere nicht aufhalten */
    }
    w.ende("abbruch");
  }
  for (const mine of [...stimmen]) {
    try {
      mine.cancel(grund);
    } catch {
      /* eine Schlange darf die andere nicht aufhalten */
    }
    stimmen.delete(mine);
  }
  kontoMeldungZurueckziehen();
}

/** Diese Schlange ist zu Ende (fertig oder abgebrochen) — sie spricht nicht mehr. Eine wartende rückt nach. */
export function stimmeEndet(t: StimmenTeilnehmer): void {
  stimmen.delete(t);
  wartende.get(t)?.ende("abbruch");
  kontoMeldungZurueckziehen();
  wartendePruefen();
}

// ── Schlange: Vespera und der Schmied fallen einander nicht ins Wort ─────

/*
 * 16.10.2026 (Martin, 13:36): „Wenn Vespera spricht und der Schmied in dem Moment eine Nachricht einpflegt, dann knallt es
 * Vespera raus und der Schmied spricht oder umgekehrt. Eine laufende Spur darf nicht unterbrochen werden.“
 *
 * Die Regel oben („wer zuletzt BEGINNT, spricht“) bleibt für zwei Antworten DESSELBEN Sprechers (eine neue Antwort Vesperas
 * löst die alte ab). Zwischen VERSCHIEDENEN Sprechern — Vespera und der Schmied — wartet die zweite Stimme, bis die erste
 * zu Ende ist, und beginnt dann (`stimmeDarfBeginnen`, aus TtsQueue vor jedem `stimmeBeginnt`).
 *
 * Belegt (Journal 16.10.): `wiedergabe(schmied) … abbruch=neue-antwort` — Vesperas getippte Antwort begann, während der
 * Schmied las. Und die Stille-Prüfung des Schmieds (app.ts `warteAufStille`, `ttsWiedergabeOffen`) sieht eine Antwort
 * Vesperas NICHT, die zwischen zwei Sätzen auf ihren Text wartet (Schlange leer, nicht fertig): dann begann der Schmied,
 * und Vesperas nächster Satz schnitt ihn ab. Diese Schlange schließt beides: Vesperas Schlange bleibt in `stimmen`, bis
 * sie ENDET (`stimmeEndet`), nicht nur, bis ihr letzter Ton verklingt.
 *
 * ⚠️ Nur auf DIESER Seite. Zwischen Seiten und Geräten entscheidet „vorn“ (unten): nur das Gerät, das gerade benutzt wird,
 *    beginnt eine Stimme — dann gibt es dort keine zweite, die warten müsste. Ohne „vorn“ gilt dort die alte Regel.
 * ⚠️ Martins Eingriffe (Stopp, Sprechtaste, neue Aufnahme, Abspielknopf) rufen `cancel` an JEDER Schlange der Seite
 *    (app.ts `stopCurrentTts`) — auch an der wartenden: ihr Signal bricht das Warten ab, die Schlange ist leer.
 * ⚠️ FRIST, damit nichts hängt: steht die erste Stimme `STIMME_SCHLANGE_STILL_MS` lang still (kein Ton, nichts in
 *    Vertonung — eine Schlange, die nie endet), oder wartet die zweite insgesamt `STIMME_SCHLANGE_MAX_MS`, beginnt sie
 *    trotzdem — mit der alten Regel (die erste endet mit `abbruch:schlange-frist`).
 */

/** So lange darf die erste Stimme STILL stehen (ohne Ton und ohne Vertonung), bevor die wartende sie ablöst. */
export const STIMME_SCHLANGE_STILL_MS = 60_000;
/** Höchstens so lange wartet eine Stimme insgesamt — länger als jede Antwort (16.10. 07:02: über 60 s am Stück). */
export const STIMME_SCHLANGE_MAX_MS = 300_000;
/** Takt, in dem die Frist prüft, ob die erste Stimme noch klingt. */
const SCHLANGE_TAKT_MS = 1000;

/** Was aus dem Warten wurde: frei (niemand im Weg), nach-warten, frist, abbruch (Martin/Seite), nicht-vorn (unten). */
export type StimmeFreigabe = "frei" | "nach-warten" | "frist" | "abbruch" | "nicht-vorn";

export interface StimmeEntscheid {
  freigabe: StimmeFreigabe;
  /** Wie lange gewartet wurde (ms). */
  gewartetMs: number;
  /** Die Journalzeile zu „vorn“ — `null`, wenn kein Gerät sich je als vorn gemeldet hat. Ohne Inhalt. */
  vornZeile: string | null;
}

interface Wartend {
  t: StimmenTeilnehmer;
  ende: (f: StimmeFreigabe) => void;
}
const wartende = new Map<StimmenTeilnehmer, Wartend>();

/** Wer spricht — „vespera“ ohne Angabe. */
function sprecherVon(t: StimmenTeilnehmer): string {
  return t.sprecher ?? "vespera";
}

/** Die Stimmen eines ANDEREN Sprechers, die gerade sprechen (begonnen, nicht beendet). */
function imWeg(t: StimmenTeilnehmer): StimmenTeilnehmer[] {
  return [...stimmen].filter((s) => s !== t && sprecherVon(s) !== sprecherVon(t));
}

function wartendePruefen(): void {
  for (const w of [...wartende.values()]) if (imWeg(w.t).length === 0) w.ende("nach-warten");
}

/** Wie viele Schlangen warten gerade? (Tests, Messung) */
export function stimmenWartend(): number {
  return wartende.size;
}

function entscheidMitVorn(f: StimmeFreigabe, gewartetMs: number, jetzt: number, t?: StimmenTeilnehmer): StimmeEntscheid {
  if (f === "abbruch") return { freigabe: f, gewartetMs, vornZeile: null };
  const vorn = vornUrteil(jetzt, t);
  return { freigabe: vorn.spricht ? f : "nicht-vorn", gewartetMs, vornZeile: vorn.zeile };
}

/**
 * Der Teil von `stimmeDarfBeginnen`, der nicht warten muss — SYNCHRON, damit das Stück einer laufenden Stimme ohne
 * zusätzlichen Takt anläuft wie bisher. `null`: es muss gewartet werden.
 */
export function stimmeEntscheidSofort(t: StimmenTeilnehmer, signal?: AbortSignal, jetzt: () => number = Date.now): StimmeEntscheid | null {
  if (stimmen.has(t)) return { freigabe: "frei", gewartetMs: 0, vornZeile: null };
  if (signal?.aborted) return entscheidMitVorn("abbruch", 0, jetzt(), t);
  if (imWeg(t).length === 0) return entscheidMitVorn("frei", 0, jetzt(), t);
  return null;
}

/**
 * Darf diese Schlange JETZT zu sprechen beginnen? Löst auf, sobald sie darf — sofort, wenn keine Stimme eines anderen
 * Sprechers spricht; sonst, wenn die letzte von ihnen endet, oder nach der Frist. Erst danach wird „vorn“ geprüft (das
 * Gerät kann inzwischen gewechselt haben). Wirft nie.
 *
 * Danach ruft die Schlange `stimmeBeginnt` (bei `frist` mit dem Grund `schlange-frist`) — oder, bei `abbruch` und
 * `nicht-vorn`, nicht: dann spricht sie nicht.
 */
export function stimmeDarfBeginnen(t: StimmenTeilnehmer, signal?: AbortSignal, jetzt: () => number = Date.now): Promise<StimmeEntscheid> {
  const sofort = stimmeEntscheidSofort(t, signal, jetzt);
  if (sofort) return Promise.resolve(sofort);
  const start = jetzt();
  const entscheid = (f: StimmeFreigabe): StimmeEntscheid => entscheidMitVorn(f, Math.max(0, jetzt() - start), jetzt(), t);
  return new Promise((ok) => {
    let stillSeit = jetzt();
    const takt = setInterval(() => {
      const n = jetzt();
      const weg = imWeg(t);
      if (weg.length === 0) return w.ende("nach-warten");
      if (weg.some((s) => s.isAudioActive?.() ?? true)) stillSeit = n;
      if (n - stillSeit >= STIMME_SCHLANGE_STILL_MS || n - start >= STIMME_SCHLANGE_MAX_MS) w.ende("frist");
    }, SCHLANGE_TAKT_MS);
    (takt as unknown as { unref?: () => void }).unref?.();
    const ab = (): void => w.ende("abbruch");
    const w: Wartend = {
      t,
      ende: (f) => {
        if (wartende.get(t) !== w) return;
        wartende.delete(t);
        clearInterval(takt);
        signal?.removeEventListener("abort", ab);
        ok(entscheid(f));
      },
    };
    wartende.set(t, w);
    signal?.addEventListener("abort", ab, { once: true });
  });
}

/** Wie viele Schlangen sprechen gerade? (Tests, Messung) */
export function stimmenAktiv(): number {
  return stimmen.size;
}

// ── Konto: eine Stimme über alle Geräte ──────────────────────────────────

/*
 * 05.10.2026, Ticket 2882 — der Kanal oben endet am Rand des Browsers. Am
 * 29.09. (07:35) und am 01.10. (21:57) sprachen Rechner und Telefon zugleich,
 * 10 und 19 Sekunden lang: zwei verschiedene Antworten, jede auf ihrem Gerät.
 *
 * Dieselbe Regel, über den Server gereicht (shared/stimme-konto.ts): jede Seite
 * meldet den Beginn ihrer Stimme, der Server vergibt je Konto eine laufende
 * Nummer und schreibt das Ereignis in den offenen Kanal jeder Seite des Kontos
 * (`/api/chat/stream`). Die Stimme mit der kleineren Nummer endet mit einem
 * Abbruch (`anderes-geraet`) — derselbe `cancel`, den der Kanal oben ruft.
 *
 * ⚠️ Die Meldung hält die Stimme nie auf: sie geht ab, während das erste Stück
 *    schon spielt. Scheitert sie (kein Netz, nicht angemeldet), spricht die
 *    Seite wie bisher.
 * ⚠️ Bis die eigene Nummer da ist, wird nichts entschieden: ein fremdes
 *    Ereignis in dieser Zeit kann vor ODER nach dem eigenen Beginn liegen
 *    (zwei Verbindungen überholen einander). Es wird gemerkt und mit der
 *    eigenen Nummer verglichen. So bleibt bei gleichzeitigem Beginn im
 *    Regelfall genau eine. Die Nummer kommt auf ZWEI Wegen: mit der Antwort
 *    auf die Meldung und mit dem eigenen Ereignis im Kanal (`eigeneNrAusKanal`)
 *    — geht die Antwort verloren, reicht das Ereignis.
 * ⚠️ KEINE Zusage „immer genau eine“ (Nachprüfung 05.10.; die Grenzen stehen
 *    in shared/stimme-konto.ts): gehen Antwort UND eigenes Ereignis verloren,
 *    weichen bei gleichzeitigem Beginn beide; eine Meldung, die schon beim
 *    Server ist, wenn ihre Stimme endet, lässt das andere Gerät einer Stimme
 *    weichen, die es nicht mehr gibt; und ein Ereignis, das in eine
 *    Neuverbindung des Kanals fällt, kommt nie an — dann bleiben zwei.
 * ⚠️ Seiten DESSELBEN Browsers regelt der Kanal oben, mit seiner eigenen
 *    Reihenfolge (die Uhr des Rechners). Der Server zählt die Ankunft — für
 *    zwei Seiten, die im selben Augenblick beginnen, könnten die beiden
 *    Reihenfolgen einander widersprechen, und dann wichen BEIDE. Darum gilt
 *    für eine Seite, die dieser Browser über den Kanal KENNT, nur der Kanal:
 *    ein Server-Ereignis von ihr wird verworfen; eines, das während der
 *    offenen Meldung gemerkt wurde, zählt nur, wenn der Kanal ihre Seite auch
 *    bei der Antwort noch nicht kennt (`fremdeSeitBeginn`, Prüfung 06.10.).
 *    Ein Restfenster bleibt: kommt das Server-Ereignis einer Seite, die der
 *    Kanal noch nie meldete, NACH der eigenen Antwort, aber VOR ihrer
 *    Kanal-Nachricht, entscheidet es sofort — dann können beide weichen.
 *    (Schließen ließe es sich, wenn jede Seite sich beim Laden im Kanal
 *    meldete; das ist nicht gebaut.)
 * ⚠️ Zurücknehmen: `STIMME_JE_KONTO=0` am Server — dann reicht er nichts weiter,
 *    und hier kommt nie ein Ereignis an.
 */

/**
 * So lange wartet eine Seite auf die Nummer ihres Beginns; danach gilt die
 * Meldung als gescheitert, und ein inzwischen gemerktes fremdes Ereignis zählt.
 *
 * Herleitung (Journal 01.–05.10.2026, Konto 1, 330 Sprachnachrichten): vom
 * Absenden bis zur Ankunft der ganzen Aufnahme (Median 165 kB) vergingen im
 * Median 0,5 s, bei 95 % höchstens 2,5 s; 8 von 330 brauchten länger als 5 s.
 * Die Meldung hier trägt keinen Inhalt und ist schneller als jede davon. 5 s
 * liegt beim Doppelten der 95 % — wer dann noch wartet, hat kein Netz.
 */
export const KONTO_MELDE_FRIST_MS = 5000;

/** Seiten, die sich über den Kanal dieses Browsers gemeldet haben — für sie gilt der Kanal, nicht der Server. */
const kanalSeiten = new Set<string>();
/** Zählt die eigenen Beginne: die Antwort auf eine ältere Meldung darf die jüngere nicht überschreiben. */
let kontoLauf = 0;
/** Die Meldung des jüngsten Beginns ist noch unterwegs. */
let kontoOffen = false;
/** Bricht die Meldung ab, die noch unterwegs ist: nach der Frist, oder wenn ihre Stimme vorher endet. */
let kontoAbbruch: AbortController | null = null;
/** Die Nummer des jüngsten eigenen Beginns aus der ANTWORT auf die Meldung — `null`, solange (oder weil) keine kam. */
let meineKontoNr: number | null = null;
/**
 * Die höchste eigene Nummer aus dem KANAL: der Server schreibt das Ereignis an
 * jede Seite des Kontos, auch an die meldende (0 = noch keines).
 *
 * Nachprüfung 05.10.: beginnen zwei Geräte zugleich und geht die Antwort an
 * das spätere verloren, hielt es sich für unbekannt und wich dem früheren —
 * das seinerseits schon gewichen war. Es sprach niemand. Der Kanal trägt die
 * Nummer ein zweites Mal.
 *
 * ⚠️ Bleibt über die Antworten hinweg stehen. Sie ist dann die Nummer einer
 *    ÄLTEREN eigenen Antwort — und damit kleiner als die jeder fremden Stimme,
 *    die seither begann: scheitert die Meldung der jüngeren, weicht sie einer
 *    solchen fremden Stimme weiter.
 */
let eigeneNrAusKanal = 0;
/**
 * Fremde Ereignisse, die ankamen, während die eigene Meldung unterwegs war: Kennung der Seite → ihre höchste Nummer.
 *
 * ⚠️ Je KENNUNG, nicht nur die höchste Nummer (Prüfung 06.10.2026): entschieden wird erst bei der Antwort, und bis
 *    dahin kann die Kanal-Nachricht derselben Seite angekommen sein. Dann gilt für sie der Kanal (`kanalSeiten`) —
 *    sonst wichen in WebKit zwei Reiter, die im selben Augenblick zu sprechen begannen, in etwa jedem fünften Lauf
 *    BEIDE: der eine über den Kanal, der andere über das gemerkte Server-Ereignis.
 */
const fremdeSeitBeginn = new Map<string, { nr: number; kennung?: string | undefined; nachrang?: boolean }>();

/** Die Nummer des jüngsten eigenen Beginns, aus Antwort oder Kanal — `null`, wenn der Server keine genannt hat. */
function eigeneKontoNr(): number | null {
  const nr = Math.max(meineKontoNr ?? 0, eigeneNrAusKanal);
  return nr > 0 ? nr : null;
}

/**
 * Auf dieser Seite spricht nichts mehr, die Meldung ist aber noch unterwegs:
 * zurückziehen.
 *
 * Nachprüfung 05.10.: Eine Stimme, die nach einer halben Sekunde gestoppt
 * wurde, deren Meldung aber erst nach zwei Sekunden beim Server ankam (die
 * Leitung stand), trug dann die GRÖSSTE Nummer des Kontos — und ein Gerät, das
 * dazwischen zu sprechen begonnen hatte, wich einer Stimme, die es nicht mehr
 * gab. Stille.
 *
 * ⚠️ Hilft nur, solange die Meldung den Server nicht erreicht hat (stehende
 *    Leitung, Rückkehr aus dem Hintergrund). Ist sie schon dort, hat er sie
 *    weitergereicht; zurückholen lässt sich das nicht.
 */
function kontoMeldungZurueckziehen(): void {
  if (stimmen.size > 0 || !kontoOffen) return;
  kontoAbbruch?.abort();
}

/** Meldet den Beginn an den Server; liefert die Nummer oder `null`. Wirft nie. `kennung`: der Beitrag (ohne Inhalt). */
async function meldeKontoBeginn(abbruch: AbortController | null, kennung?: string, nachrang = false): Promise<number | null> {
  if (typeof fetch !== "function") return null;
  const frist = abbruch ? setTimeout(() => abbruch.abort(), KONTO_MELDE_FRIST_MS) : null;
  const rumpf = !!kennung || nachrang;
  try {
    const r = await fetch(STIMME_BEGINN_WEG, {
      method: "POST",
      credentials: "include",
      headers: rumpf ? { [GERAET_KOPF]: EIGENES_GERAET, "Content-Type": "application/json" } : { [GERAET_KOPF]: EIGENES_GERAET },
      ...(rumpf ? { body: JSON.stringify({ ...(kennung ? { kennung } : {}), ...(nachrang ? { nachrang: true } : {}) }) } : {}),
      ...(abbruch ? { signal: abbruch.signal } : {}),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { nr?: unknown; reihenfolge?: unknown } | null;
    // 28.10.2026: der Server sagt, ob die feste Reihenfolge gilt (Schalter REIHENFOLGE_WIEDERGABE) — nur mit Nummer (sonst ist er aus).
    const nr = j?.nr;
    if (typeof nr === "number") reihenfolgeAus = j?.reihenfolge === false;
    if (reihenfolgeAus) besitzAbgeben(false);
    return typeof nr === "number" && Number.isFinite(nr) && nr > 0 ? nr : null;
  } catch {
    return null;
  } finally {
    if (frist !== null) clearTimeout(frist);
  }
}

/** Eine Stimme dieser Seite beginnt (aus `stimmeBeginnt`): dem Konto melden. */
function kontoBeginn(kennung?: string, nachrang = false): void {
  const lauf = ++kontoLauf;
  kontoOffen = true;
  meineKontoNr = null;
  fremdeSeitBeginn.clear();
  const abbruch = typeof AbortController === "function" ? new AbortController() : null;
  kontoAbbruch = abbruch;
  void meldeKontoBeginn(abbruch, kennung, nachrang).then((nr) => {
    if (lauf !== kontoLauf) return;
    kontoOffen = false;
    kontoAbbruch = null;
    meineKontoNr = nr;
    // 28.10.2026 („Besitz“): die Nummer des ersten Beginns der eigenen Reihe — bei zwei Besitzern gewinnt die frühere. Erst mit ihr
    // geht die erste Besitz-Meldung hinaus (eine Meldung ohne Nummer gälte bei den anderen als „unbekannt“).
    if (eigenerBesitz && !eigenerBesitz.gemeldet) {
      eigenerBesitz.nr = nr;
      eigenerBesitz.gemeldet = true;
      letzteBehauptung = Date.now();
      meldeBesitz(true);
    }
    // Erst JETZT prüfen, wen der Kanal dieses Browsers kennt: seine Nachricht kann nach dem Server-Ereignis gekommen sein.
    let weichen: { seite: string; nr: number } | null = null;
    for (const [seite, f] of fremdeSeitBeginn) {
      if (!kanalSeiten.has(seite) && f.nr > 0 && fremdeKontoStimmeGewinnt(seite, f.nr, f.kennung, f.nachrang !== true)) weichen = { seite, nr: f.nr };
    }
    if (weichen) {
      stimmenBeenden("anderes-geraet");
      nachWeichen(weichen.seite, weichen.nr);
    } else if (fremdeSeitBeginn.size > 0) besitzBehaupten();
  });
}

/**
 * 26.10.2026 (Journal: „abgeschnitten und dann nochmal von vorn“, Mac + iPhone): derselbe Beitrag (gleiche Kennung) auf zwei
 * Geräten. Das ZUERST begonnene spricht durch; ein Gerät, das als „vorn“ gemeldet ist, geht vor. Ein ANDERER Beitrag löst
 * die laufende Stimme weiter ab (neue Antwort, neuer Strom). Liefert `true` (fremd gewinnt), `false` (eigene gewinnt) oder
 * `null` (kein Vorn-Urteil: dann entscheidet die Reihenfolge des Beginns).
 */
function vornVorrangGleicherBeitrag(fremdGeraet: string, jetzt = Date.now()): boolean | null {
  const fremdVorn = !!vornFremd && vornFremd.geraet === fremdGeraet && jetzt - vornFremd.seit <= VORN_GILT_MS;
  if (fremdVorn && !ichVorn) return true;
  if (ichVorn && !fremdVorn) return false;
  return null;
}

/** Gewinnt die fremde Konto-Stimme (Server-Nummer `nr`) gegen die eigene? Beachtet „gleicher Beitrag“ (oben). */
function fremdeKontoStimmeGewinnt(geraet: string, nr: number, kennung: string | undefined, vorrang = true): boolean {
  const gleich = !!kennung && kennung === meineKennung;
  if (gleich) {
    const v = vornVorrangGleicherBeitrag(geraet);
    if (v !== null) return v;
  }
  // 28.10.2026 („Besitz“): diese Seite besitzt die Reihe — ein Entwickler-Beitrag, der am anderen Gerät von selbst begann
  // (`nachrang`), löst sie nie ab. Sonst (Vesperas Antwort dort, Martins Knopf): die alte Regel.
  if (!gleich && reihenfolgeAn() && eigenerBesitz && !vorrang) return false;
  return gewinntFremdeKontoStimme(eigeneKontoNr(), nr, gleich);
}

/** Kennung → wann ein ANDERES Gerät begann, sie zu sprechen (Uhr dieser Seite). Die eigene Seite steht hier nie. */
const fremdGesprochen = new Map<string, number>();

function merkeFremd(kennung: string, jetzt = Date.now()): void {
  fremdGesprochen.set(kennung, jetzt);
  for (const [k, um] of fremdGesprochen) if (jetzt - um > GESPROCHEN_MERKEN_MS) fremdGesprochen.delete(k);
}

/**
 * 26.10.2026: läuft dieser Beitrag schon auf einem ANDEREN Gerät oder wurde er dort zuletzt gesprochen? Dann beginnt diese
 * Seite ihn nicht (auch nicht im sofortigen Fall, `stimmeBeginnt`). Nicht gesperrt: die Seite, die „vorn“ ist, und
 * Martins Knopf (der Aufrufer prüft `ausdruecklich`). Wiederholen auf demselben Gerät ist nie gesperrt (nur Fremdes zählt).
 */
export function beitragAnderswoLaeuft(kennung: string | undefined, jetzt = Date.now()): boolean {
  if (!kennung || ichVorn) return false;
  const um = fremdGesprochen.get(kennung);
  return um !== undefined && jetzt - um <= GESPROCHEN_MERKEN_MS;
}

/**
 * Der Server meldet: irgendeine Seite dieses Kontos hat zu sprechen begonnen
 * (aus dem Kanal `/api/chat/stream`, frontend/lib/chat-stream.ts `onStimme`).
 * Begann sie NACH der eigenen Stimme, endet die eigene mit `anderes-geraet`.
 */
export function stimmeVomKonto(e: StimmeEreignis, jetzt = Date.now()): void {
  // 17.10.2026: eine andere Seite spricht DIESEN Beitrag — vor jeder Rückkehr unten, auch für Seiten desselben Browsers.
  if (e.geraet !== EIGENES_GERAET && e.kennung) {
    merkeGesprochen(e.kennung);
    merkeFremd(e.kennung);
  }
  if (e.geraet === EIGENES_GERAET) {
    // Das eigene Ereignis beendet nichts — es nennt die eigene Nummer ein zweites Mal (siehe `eigeneNrAusKanal`).
    eigeneNrAusKanal = Math.max(eigeneNrAusKanal, e.nr);
    return;
  }
  besitzNachFremdemBeginn(e, jetzt);
  if (kanalSeiten.has(e.geraet)) return;
  if (stimmen.size === 0) return;
  if (kontoOffen) {
    const alt = fremdeSeitBeginn.get(e.geraet);
    if (!alt || e.nr >= alt.nr) fremdeSeitBeginn.set(e.geraet, { nr: e.nr, kennung: e.kennung, nachrang: e.nachrang === true });
    return;
  }
  if (fremdeKontoStimmeGewinnt(e.geraet, e.nr, e.kennung, e.nachrang !== true)) {
    stimmenBeenden("anderes-geraet");
    nachWeichen(e.geraet, e.nr, jetzt);
  } else besitzBehaupten(jetzt);
}

// ── Vorn: nur das Gerät spricht, das gerade benutzt wird ─────────────────

/*
 * 16.10.2026 (Martin, 08:09): „es soll immer nur das Gerät sprechen, das aktiv benutzt wird, entweder die native App oder
 * der MacBook, je nachdem welches Gerät gerade aktiv ist“. Journal 13:32–13:38: Mac und iPhone offen, der Schmied las auf
 * BEIDEN vor, und jede neue Stimme des einen beendete die des anderen (`abbruch=anderes-geraet`, viermal in sechs Minuten).
 *
 * Eine Seite wird VORN durch Benutzung: Berührung oder Taste, Fokus, sichtbar werden, eine Aufnahme (auch Wach-Wort im
 * Hintergrund) — `geraetAktiv`. Sie meldet es dem Server (`STIMME_VORN_WEG`), der es an jede Seite des Kontos reicht.
 * Eine Seite, die NICHT vorn ist, beginnt keine Stimme (`vornUrteil` in `stimmeDarfBeginnen`): ihre Schlange endet mit
 * `abbruch:nicht-vorn`, der Text bleibt sichtbar.
 *
 * RÜCKFALL „sprechen“ (nie Stille auf allen Geräten):
 *  - hat sich kein Gerät als vorn gemeldet (Konto ≠ 1, Server aus, Einzelgerät, Tests) — wie bisher;
 *  - die Meldung des vorderen Geräts ist älter als `VORN_GILT_MS` (es erneuert sie alle `VORN_TAKT_MS`, solange es
 *    sichtbar ist — ein geschlossenes oder gesperrtes Gerät verstummt also nach spätestens einer Minute als „vorn“);
 *  - das vordere Gerät ging in den Hintergrund (`an: false`);
 *  - DIESE Seite wurde ECHT bedient (Tippen, Taste, Aufnahme), seit die fremde Meldung ankam (ihre eigene Meldung ist noch
 *    unterwegs) — ein Abspielknopf auf dem Gerät im Hintergrund spielt also sofort.
 *
 * HAFTEN (16.10.2026 abends, Martin 18:31 — shared/stimme-konto.ts): sichtbar, Fokus und Takt meldet die Seite weiter, der
 * Server lässt sie aber kein anderes Gerät verdrängen; und hier zählen sie nicht als „eigene Eingabe“ (vorher sprach ein
 * Telefon, dessen Bildschirm anging, sofort mit). Weist der Server ab, nennt er das vordere Gerät (`vorn` in der Antwort),
 * und die Seite richtet sich danach. `STIMME_VORN_HAFTET=0` am Server: die Antwort trägt `haftet: false`, alles wie vorher.
 * ⚠️ Gemessen wird mit der Uhr DIESER Seite (Ankunft der Meldung), nie mit der Uhr des anderen Geräts.
 */

/** So lange gilt die Meldung „vorn“ eines anderen Geräts ohne Erneuerung. */
export const VORN_GILT_MS = 60_000;
/** So oft erneuert die vordere Seite ihre Meldung (sichtbar) — und höchstens so oft meldet eine Berührung erneut. */
export const VORN_TAKT_MS = 20_000;

/**
 * Ein anderes Gerät hat sich als vorn gemeldet: Kennung und Ankunft (Uhr dieser Seite). `echtUm`: wann es zuletzt mit ECHTER
 * Bedienung gemeldet wurde (`VornEreignis.echt`, 28.10.2026 Prüfbefund M2) — `-Infinity`, solange keine kam.
 */
let vornFremd: { geraet: string; seit: number; echtUm: number } | null = null;
/** Der Server hat diese Seite zuletzt als vorn genannt. */
let ichVorn = false;
/** Wann diese Seite zuletzt ECHT bedient wurde (Uhr dieser Seite; Schalter aus: jede Benutzung). */
let eigeneAktivSeit = -Infinity;
/** Bis wann der Server von der echten Bedienung weiß (die letzte Meldung mit echtem Grund). */
let echtGemeldetBis = -Infinity;
/** Haftet „vorn“ (Server-Schalter `STIMME_VORN_HAFTET`)? Bis der Server anderes sagt: ja. */
let vornHaftet = true;
/** Wann diese Seite zuletzt „vorn“ gemeldet hat. */
let letzteVornMeldung = -Infinity;
/** Der Server sagt, „vorn“ gilt für dieses Konto nicht — dann meldet die Seite nichts mehr. */
let vornAus = false;
let vornTakt: ReturnType<typeof setInterval> | null = null;

function kurzKennung(g: string): string {
  return g.slice(-4);
}

/**
 * Darf DIESE Seite jetzt sprechen? Mit der Journalzeile (`null`, wenn nie ein Gerät vorn war). Rein bis auf den Zustand.
 * `t` (die Stimme, die beginnen will): 28.10.2026 „Besitz“ — die Seite, deren Reihe läuft, spricht weiter, auch wenn ein anderes
 * Gerät vorn wurde; solange die Reihe eines ANDEREN Geräts läuft, beginnt hier keine Stimme von selbst (Martins Knopf und
 * Vesperas Antwort nach seiner Bedienung HIER schon). Ohne `t` (Mikro, Soniox-Vorlauf): nur „vorn“ wie bisher.
 */
function vornUrteil(jetzt: number, t?: StimmenTeilnehmer): { spricht: boolean; zeile: string | null } {
  const zeile = (vorn: string, spricht: boolean, grund: string): string =>
    `[stimme-geraet] vorn=${vorn} spricht=${spricht ? "ja" : "nein"} grund=${grund}`;
  if (t && reihenfolgeAn()) {
    // Nur, solange die eigene Reihe wirklich läuft (nicht eine verklungene, deren Takt das Ende noch nicht gemeldet hat).
    if (eigenerBesitz && laeuftNoch()) {
      const v = vornUrteilOhneBesitz(jetzt, zeile);
      if (v.spricht) return v;
      // Prüfbefund M2 (28.10.2026): der Besitz beginnt keinen NEUEN Beitrag, solange Martin am anderen Gerät aufnimmt oder es
      // zuletzt echt bedient hat — sonst spräche diese Seite in seine Aufnahme (Echo, Verhörer). Ein laufender Satz fragt hier
      // nie (`stimmeEntscheidSofort`: schon in `stimmen` → frei) und spricht zu Ende.
      const hand = handAmAnderenGeraet(jetzt);
      if (hand) return { spricht: false, zeile: zeile(kurzKennung(hand.geraet), false, hand.grund) };
      return { spricht: true, zeile: zeile(kurzKennung(EIGENES_GERAET), true, "besitz") };
    }
    const b = besitzFremdAktiv(jetzt);
    if (b && !stimmeHatVorrang(t)) {
      return { spricht: false, zeile: zeile(kurzKennung(b.geraet), false, "anderes-geraet-spricht") };
    }
  }
  return vornUrteilOhneBesitz(jetzt, zeile);
}

/**
 * Ist Martins Hand an einem ANDEREN Gerät (Prüfbefund M2)? Es nimmt laut Server auf (`fremdeGeraeteAufnahmen`, jeder Weg) —
 * oder es ist vorn und seine letzte echte Bedienung ist jünger als die eigene. Sonst `null`. Rein bis auf den Zustand.
 */
function handAmAnderenGeraet(jetzt: number): { geraet: string; grund: "aufnahme-anderes-geraet" | "bedienung-anderes-geraet" } | null {
  const a = fremdesGeraetNimmtAuf(jetzt);
  if (a) return { geraet: a.geraet, grund: "aufnahme-anderes-geraet" };
  const v = vornFremd;
  if (v && jetzt - v.seit <= VORN_GILT_MS && v.echtUm > eigeneAktivSeit) return { geraet: v.geraet, grund: "bedienung-anderes-geraet" };
  return null;
}

function vornUrteilOhneBesitz(
  jetzt: number,
  zeile: (vorn: string, spricht: boolean, grund: string) => string,
): { spricht: boolean; zeile: string | null } {
  const v = vornFremd;
  if (!v) return { spricht: true, zeile: ichVorn ? zeile(kurzKennung(EIGENES_GERAET), true, "selbst") : null };
  if (jetzt - v.seit > VORN_GILT_MS) return { spricht: true, zeile: zeile(kurzKennung(v.geraet), true, "veraltet") };
  if (eigeneAktivSeit >= v.seit) return { spricht: true, zeile: zeile(kurzKennung(EIGENES_GERAET), true, "eigene-eingabe") };
  return { spricht: false, zeile: zeile(kurzKennung(v.geraet), false, "anderes-geraet-vorn") };
}

/**
 * 17.10.2026 (Martin, 07:35: „die Stimme spielte am Handy, gleichzeitig ging am Laptop das Mikrofon von selbst auf“): darf
 * das Mikro auf DIESER Seite VON SELBST aufgehen (nach einer Frage/Antwort, `nach-frage`/`nach-antwort`)? Nicht, solange ein
 * anderes Gerät vorn ist — dort spricht die Stimme, und dort geht das Mikro nach ihrem Ende auf. Liefert die Kurzkennung des
 * vorderen Geräts (dann bleibt es zu), sonst `null`. Dieselbe Regel wie für die Stimme (`vornUrteil`, mit ihrem Rückfall:
 * niemand vorn, veraltet, Hintergrund, eigene echte Bedienung → `null`). Martins Hand (Knopf, Wach-Wort) fragt hier nie.
 */
export function autoMikroNichtVorn(jetzt = Date.now()): string | null {
  const v = vornFremd;
  return v && !vornUrteil(jetzt).spricht ? kurzKennung(v.geraet) : null;
}

/**
 * 17.10.2026 (Journal: 5–10 s Pause vor einzelnen Sätzen): steht diese Seite JETZT hinter einem anderen Gerät, das vorn ist?
 * Dann beginnt sie keine Stimme (sie parkt oder verwirft) — und startet darum auch keinen Soniox-Strom, der die Render-Sperre
 * des Servers hielte, während die vordere Seite denselben Text rendern will. Ohne Meldung eines vorderen Geräts: `false`.
 */
export function seiteNichtVorn(jetzt = Date.now()): boolean {
  return autoMikroNichtVorn(jetzt) !== null;
}

/** Meldet „vorn“ (oder „nicht mehr vorn“) an den Server. Wirft nie, hält nichts auf. */
function meldeVorn(an: boolean, grund: string): void {
  if (vornAus || typeof fetch !== "function") return;
  try {
    void fetch(STIMME_VORN_WEG, {
      method: "POST",
      credentials: "include",
      headers: { [GERAET_KOPF]: EIGENES_GERAET, "Content-Type": "application/json" },
      body: JSON.stringify({ an, grund }),
      keepalive: true,
    })
      .then(async (r) => {
        if (!r.ok) return;
        const j = (await r.json().catch(() => null)) as { aus?: unknown; haftet?: unknown; vorn?: unknown } | null;
        if (j?.aus === true) {
          vornAus = true;
          if (vornTakt !== null) clearInterval(vornTakt);
          vornTakt = null;
        }
        if (j?.haftet === false) vornHaftet = false;
        // Abgewiesen: ein anderes Gerät bleibt vorn. Wie ein Ereignis aus dem Kanal behandeln (das evtl. verpasst wurde).
        if (typeof j?.vorn === "string" && j.vorn.length > 0 && j.vorn !== EIGENES_GERAET) {
          vornVomKonto({ geraet: j.vorn, an: true });
        }
      })
      .catch(() => undefined);
  } catch {
    /* ohne Netz bleibt es beim Rückfall */
  }
}

function sichtbar(): boolean {
  try {
    return typeof document === "undefined" || document.visibilityState !== "hidden";
  } catch {
    return true;
  }
}

/**
 * Diese Seite wird benutzt (Berührung, Taste, Fokus, sichtbar, Aufnahme, Abspielknopf) — die anderen Geräte erfahren es.
 * Sofort sprechen darf sie nur nach ECHTER Bedienung (`vornGrundEcht`; Schalter aus: nach jeder). Gemeldet wird höchstens
 * alle `VORN_TAKT_MS` — außer ein anderes Gerät ist vorn, oder echte Bedienung trifft eine Seite, die nicht vorn ist.
 */
export function geraetAktiv(grund: string, jetzt = Date.now()): void {
  const echt = !vornHaftet || vornGrundEcht(grund);
  if (echt) {
    eigeneAktivSeit = jetzt;
    geparktePruefen(jetzt); // Martin bedient DIESE Seite: ein zurückgehaltener Beitrag spricht jetzt hier („Geparkt“ unten)
  }
  if (vornFremd === null && (ichVorn || !echt || !vornHaftet) && jetzt - letzteVornMeldung < VORN_TAKT_MS) return;
  letzteVornMeldung = jetzt;
  if (echt) echtGemeldetBis = jetzt;
  meldeVorn(true, grund);
}

/** Die vordere Seite erneuert ihre Meldung, solange sie sichtbar ist — sonst gälte sie nach einer Minute als veraltet. */
function vornTaktSetzen(an: boolean): void {
  if (!an || vornAus) {
    if (vornTakt !== null) clearInterval(vornTakt);
    vornTakt = null;
    return;
  }
  if (vornTakt !== null) return;
  vornTakt = setInterval(() => {
    if (!sichtbar()) return;
    letzteVornMeldung = Date.now();
    // Bedienung, die der Drossel zum Opfer fiel, reist mit dem Takt — sonst hielte der Server sie für älter (10-min-Regel).
    const echt = eigeneAktivSeit > echtGemeldetBis;
    if (echt) echtGemeldetBis = eigeneAktivSeit;
    meldeVorn(true, echt ? "eingabe" : "takt");
  }, VORN_TAKT_MS);
  (vornTakt as unknown as { unref?: () => void }).unref?.();
}

/** Diese Seite geht in den Hintergrund: war sie vorn, gilt kein Gerät mehr als vorn (Rückfall „sprechen“). */
export function geraetHinten(): void {
  if (!ichVorn && letzteVornMeldung === -Infinity) return;
  letzteVornMeldung = -Infinity;
  meldeVorn(false, "hintergrund");
}

/** Der Server meldet: dieses Gerät ist jetzt vorn (`an`) — oder nicht mehr. Aus dem Kanal (chat-stream.ts `onStimmeVorn`). */
export function vornVomKonto(e: VornEreignis, jetzt = Date.now()): void {
  if (e.geraet === EIGENES_GERAET) {
    ichVorn = e.an;
    if (e.an) vornFremd = null;
    vornTaktSetzen(e.an);
    if (e.an) geparktePruefen(jetzt);
    return;
  }
  if (e.an) {
    const echtVorher = vornFremd?.geraet === e.geraet ? vornFremd.echtUm : -Infinity;
    vornFremd = { geraet: e.geraet, seit: jetzt, echtUm: e.echt === true ? jetzt : echtVorher };
    ichVorn = false;
    vornTaktSetzen(false);
  } else if (vornFremd?.geraet === e.geraet) {
    vornFremd = null;
  }
}

/*
 * ⚠️ Die Hörer stehen hier, nicht in app.ts: „vorn“ gehört zur einen Stimme. Erfasst wird nur, DASS benutzt wurde —
 *    kein Ziel, kein Inhalt. Mit `capture`: ein Knopf, der das Ereignis anhält, zählt trotzdem.
 */
try {
  const aktiv = (grund: string) => (): void => geraetAktiv(grund);
  window.addEventListener("pointerdown", aktiv("eingabe"), { capture: true, passive: true });
  window.addEventListener("keydown", aktiv("eingabe"), { capture: true, passive: true });
  window.addEventListener("focus", aktiv("fokus"));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") geraetAktiv("sichtbar");
    else geraetHinten();
  });
  // Seite zu (nicht nur verdeckt): ist sie vorn, gibt der Server „vorn“ sofort frei — sonst haftete es bis zu 10 Minuten.
  window.addEventListener("pagehide", (e) => {
    if (!e.persisted) meldeVorn(false, "zu");
  });
} catch {
  /* ohne window (Tests in node) gibt es keine Benutzung */
}

// ── Besitz: das Gerät, das die Wiedergabe begann, behält sie ─────────────

/*
 * 28.10.2026 (Martin, 07:54: „Während Vespera spricht, funkt plötzlich der Zimmermann hinein, und dann sprang die Audiospur
 * von meinem Computer direkt aufs Handy. Das Ganze braucht eine logische Abfolge.“ — shared/stimme-konto.ts „BESITZ“).
 *
 * Ursache: Entwickler-Beiträge warten auf „Stille“ (app.ts `warteAufStille`) — aber nur auf DIESER Seite. Am Telefon klang
 * nichts, also las es Zimmermanns Beitrag vor (der Mac galt nicht als vorn: Hintergrund oder veraltet → Rückfall „sprechen“).
 * Dessen Beginn kam am Mac als neuere Stimme eines ANDEREN Beitrags an (`stimmeVomKonto`), und „wer zuletzt beginnt,
 * spricht“ beendete Vesperas laufende Antwort (`abbruch:anderes-geraet`). Die Spur sprang aufs Handy.
 *
 * Jetzt (Schalter am Server `REIHENFOLGE_WIEDERGABE=0` → alles wie vorher):
 *  - Wer eine Stimme beginnt, BESITZT die Reihe (`eigenerBesitz`), solange hier etwas spricht, wartet (Schlange) oder die
 *    Vorlese-Reihe der Entwickler offen ist (`setzeReiheOffenQuelle`). Er meldet das alle `BESITZ_TAKT_MS` und am Ende einmal
 *    `besitz: false`. Er spricht seine Reihe auch dann weiter, wenn ein anderes Gerät inzwischen vorn wurde — aber nur, solange
 *    Martins Hand nicht dort ist: nimmt das andere Gerät auf oder wurde es zuletzt ECHT bedient (`VornEreignis.echt`), beginnt
 *    der Besitzer keinen neuen Beitrag (er parkt ihn); ein laufender Satz spricht zu Ende (Prüfbefund M2).
 *  - Jede andere Seite merkt sich die fremde Reihe (`besitzFremd`, aus der Besitz-Meldung) und beginnt solange keinen
 *    Entwickler-Beitrag von selbst (`vornUrteil` → `anderes-geraet-spricht`; mit Kennung parkt er und verfällt, wenn der
 *    Besitzer ihn spricht). Sein Beginn trägt `nachrang` und löst eine fremde Reihe nie ab. Vesperas Antwort (sie beginnt dort,
 *    wo Martin fragte) und Martins Abspielknopf gehen wie bisher vor (`stimmeHatVorrang`, Ticket 2882 unverändert).
 *  - Ein Gerät, das sich `BESITZ_GILT_MS` nicht meldet, ist weg — dann gilt die alte Regel.
 *  - Begannen zwei Seiten im selben Augenblick einen Entwickler-Beitrag, gewinnt die frühere Reihe (`besitzFremdGewinnt`): die besitzende
 *    Seite weicht keinem fremden Beginn, sondern meldet sofort ihren Besitz (`besitzBehaupten`), und die spätere weicht.
 * ⚠️ Nur Kennungen und Nummern, nie Inhalt. Ohne Netz: Rückfall wie vorher.
 */

/** Die eigene laufende Reihe: seit wann, die Konto-Nummer ihres ersten Beginns (sobald bekannt), und ob sie schon gemeldet ist. */
let eigenerBesitz: { seit: number; nr: number | null; gemeldet: boolean } | null = null;
/** Die Reihe eines ANDEREN Geräts: Kennung, Nummer, erste und letzte Meldung (Uhr dieser Seite). */
let besitzFremd: { geraet: string; nr: number | null; seit: number; zuletzt: number } | null = null;
/** Die jüngste eigene Stimme hat Vorrang (kein Entwickler-Beitrag von selbst, `stimmeHatVorrang`). */
let meinVorrang = false;
/** Der Server sagt `REIHENFOLGE_WIEDERGABE=0` — dann gilt alles wie vor dem 28.10.2026. */
let reihenfolgeAus = false;
/** Der Server reicht nichts weiter (`STIMME_JE_KONTO=0`) — dann meldet die Seite keinen Besitz. */
let besitzAus = false;
let besitzTakt: ReturnType<typeof setInterval> | null = null;
let letzteBehauptung = -Infinity;
/** Höchstens so oft eine sofortige Besitz-Meldung auf einen fremden Beginn (der Takt läuft daneben weiter). */
const BEHAUPTEN_ABSTAND_MS = 250;
/** Ist die Vorlese-Reihe der Entwickler (app.ts) noch offen? Zwischen zwei Beiträgen bleibt der Besitz so bestehen. */
let reiheOffenQuelle: () => boolean = () => false;

/** Gilt die feste Reihenfolge (Server-Schalter `REIHENFOLGE_WIEDERGABE`, Vorgabe an)? */
export function reihenfolgeAn(): boolean {
  return !reihenfolgeAus;
}

/** app.ts: woran die Seite erkennt, dass ihre Vorlese-Reihe noch Beiträge hat. */
export function setzeReiheOffenQuelle(offen: () => boolean): void {
  reiheOffenQuelle = offen;
}

/** Wessen Reihe läuft? (Tests, Messung) */
export function besitzStand(jetzt = Date.now()): { eigen: boolean; fremd: string | null } {
  return { eigen: eigenerBesitz !== null, fremd: besitzFremdAktiv(jetzt)?.geraet ?? null };
}

function besitzFremdAktiv(jetzt: number): { geraet: string; nr: number | null; seit: number; zuletzt: number } | null {
  const b = besitzFremd;
  return b && jetzt - b.zuletzt <= BESITZ_GILT_MS ? b : null;
}

/**
 * Hat diese Stimme Vorrang vor der Reihe eines anderen Geräts? Martins Abspielknopf — und jede Stimme, die KEIN Entwickler-
 * Beitrag ist: Vesperas Antwort beginnt dort, wo Martin gefragt hat (dort gilt weiter „wer zuletzt beginnt, spricht“, Ticket
 * 2882). Nur ein Entwickler-Beitrag, der von selbst beginnt (`sprecher: "schmied"`), ordnet sich der fremden Reihe unter. Rein.
 */
export function stimmeHatVorrang(t: { readonly sprecher?: string | undefined; readonly ausdruecklich?: boolean | undefined }): boolean {
  return t.ausdruecklich === true || t.sprecher !== "schmied";
}

function laeuftNoch(): boolean {
  if (stimmen.size > 0 || wartende.size > 0) return true;
  try {
    return reiheOffenQuelle();
  } catch {
    return false;
  }
}

function merkeBesitzFremd(geraet: string, nr: number | null, zuletzt: number): void {
  const alt = besitzFremd;
  const gleich = alt?.geraet === geraet ? alt : null;
  const seit = gleich && zuletzt - gleich.zuletzt <= BESITZ_GILT_MS ? gleich.seit : zuletzt;
  besitzFremd = { geraet, nr, seit, zuletzt: Math.max(zuletzt, gleich?.zuletzt ?? -Infinity) };
}

/** Meldet die eigene Reihe (läuft / zu Ende) dem Server. Wirft nie, hält nichts auf. */
function meldeBesitz(an: boolean): void {
  if (besitzAus || reihenfolgeAus || typeof fetch !== "function") return;
  const reiheNr = an ? (eigenerBesitz?.nr ?? null) : null;
  try {
    void fetch(STIMME_BESITZ_WEG, {
      method: "POST",
      credentials: "include",
      headers: { [GERAET_KOPF]: EIGENES_GERAET, "Content-Type": "application/json" },
      body: JSON.stringify({ besitz: an, ...(reiheNr !== null ? { reiheNr } : {}) }),
      keepalive: true,
    })
      .then(async (r) => {
        if (!r.ok) return;
        const j = (await r.json().catch(() => null)) as { aus?: unknown; reihenfolge?: unknown } | null;
        if (j?.aus === true) besitzAus = true;
        if (j?.reihenfolge === false) reihenfolgeAus = true;
        if (besitzAus || reihenfolgeAus) besitzAbgeben(false);
      })
      .catch(() => undefined);
  } catch {
    /* ohne Netz bleibt es beim Rückfall */
  }
}

/** Diese Seite beginnt eine Stimme: ihre Reihe beginnt (oder läuft weiter). */
function besitzUebernehmen(jetzt: number): void {
  if (!reihenfolgeAn()) return;
  besitzFremd = null;
  if (eigenerBesitz) return;
  // Gemeldet wird, sobald die Nummer des Beginns da ist (`kontoBeginn`), danach im Takt.
  eigenerBesitz = { seit: jetzt, nr: null, gemeldet: false };
  if (!besitzAus && besitzTakt === null) {
    besitzTakt = setInterval(besitzTaktSchlag, BESITZ_TAKT_MS);
    (besitzTakt as unknown as { unref?: () => void }).unref?.();
  }
  // Ein hier zurückgehaltener Beitrag gehört jetzt zur eigenen Reihe (er wartet in der Schlange, nie über die Stimme).
  geparktePruefen(jetzt);
}

function besitzTaktSchlag(): void {
  if (!eigenerBesitz) return besitzAbgeben(false);
  if (laeuftNoch()) {
    letzteBehauptung = Date.now();
    meldeBesitz(true);
    // Prüfbefund M2: eine fremde Aufnahme kann auch ohne Ende-Meldung verfallen — dann darf ein zurückgehaltener Beitrag.
    geparktePruefen(letzteBehauptung);
  } else besitzAbgeben(true);
}

/** Die eigene Reihe endet (`melden`: einmal `besitz: false` an die anderen) — oder wurde abgelöst. */
function besitzAbgeben(melden: boolean): void {
  const war = eigenerBesitz !== null;
  eigenerBesitz = null;
  if (besitzTakt !== null) clearInterval(besitzTakt);
  besitzTakt = null;
  if (war && melden) meldeBesitz(false);
}

/** Ein fremder Beginn traf die eigene Reihe, und sie bleibt: sofort sagen (die andere Seite weicht). Gedrosselt gegen eine Flut. */
function besitzBehaupten(jetzt = Date.now()): void {
  if (!reihenfolgeAn() || !eigenerBesitz?.gemeldet || jetzt - letzteBehauptung < BEHAUPTEN_ABSTAND_MS) return;
  letzteBehauptung = jetzt;
  meldeBesitz(true);
}

/** Diese Seite ist einer fremden Stimme gewichen (`anderes-geraet`): deren Reihe gilt jetzt. */
function nachWeichen(geraet: string, nr: number, jetzt = Date.now()): void {
  if (!reihenfolgeAn()) return;
  besitzAbgeben(false);
  merkeBesitzFremd(geraet, nr, jetzt);
}

/**
 * Ein fremder Beginn kam (Server) und diese Seite besitzt die Reihe, spricht aber gerade nicht (zwischen zwei Beiträgen):
 * Vesperas Antwort oder Martins Knopf dort übernimmt, ein Entwickler-Beitrag (`nachrang`) nicht — dann meldet sie sofort.
 * (Die fremde Reihe selbst merkt sich eine Seite NUR aus der Besitz-Meldung: mit REIHENFOLGE_WIEDERGABE=0 kommt keine, und
 * auch eine Seite, die selbst noch nie sprach, verhält sich dann wie vorher.)
 */
function besitzNachFremdemBeginn(e: StimmeEreignis, jetzt: number): void {
  if (!reihenfolgeAn() || !eigenerBesitz) return;
  if (stimmen.size > 0 || kanalSeiten.has(e.geraet)) return;
  if (e.nachrang !== true) nachWeichen(e.geraet, e.nr, jetzt);
  else besitzBehaupten(jetzt);
}

/** Der Server meldet: die Reihe eines Geräts läuft (oder endete). Aus dem Kanal (chat-stream.ts `onStimmeBesitz`). */
export function besitzVomKonto(e: BesitzEreignis, jetzt = Date.now()): void {
  if (e.geraet === EIGENES_GERAET || !reihenfolgeAn()) return;
  if (!e.besitz) {
    if (besitzFremd?.geraet === e.geraet) {
      besitzFremd = null;
      geparktePruefen(jetzt);
    }
    return;
  }
  const zuletzt = jetzt - Math.min(Math.max(0, e.alterMs ?? 0), BESITZ_GILT_MS);
  if (eigenerBesitz) {
    // Seiten desselben Browsers regelt der Kanal; zwei Geräte: die frühere Reihe gewinnt, Martins Hand hier nie.
    if (kanalSeiten.has(e.geraet) || meinVorrang) return;
    // Die eigene Nummer ist noch unterwegs (der Beginn eben erst gemeldet): die gemeldete fremde Reihe ist älter — weichen.
    const unbekannt = !eigenerBesitz.gemeldet && e.reiheNr !== undefined;
    const eigen = { nr: eigenerBesitz.nr, geraet: EIGENES_GERAET };
    if (!unbekannt && !besitzFremdGewinnt({ nr: e.reiheNr ?? null, geraet: e.geraet }, eigen)) return;
    besitzAbgeben(false);
    stimmenBeenden("besitz-anderes-geraet");
  }
  merkeBesitzFremd(e.geraet, e.reiheNr ?? null, zuletzt);
}

// ── Geparkt: kein Beitrag bleibt auf ALLEN Seiten stumm ─────────────────

/*
 * 17.10.2026 (Martin, 07:30 — zwei Seiten offen, 56cb und d68c, drei Beiträge des Schmieds, KEINER zu hören). Journal:
 *   07:28:49  vorn=56cb (Martin tippt dort)
 *   07:29:21–25  drei Beiträge; BEIDE Seiten vertonen sie ganz (Ganz-Modus, bis 25 s — doppelt, also langsamer)
 *   07:29:36–37  d68c ist fertig: `spricht=nein grund=anderes-geraet-vorn`, verworfen (`abbruch=nicht-vorn`) — richtig, ABER
 *               dann ging dort das Mikro auf (`nach-frage schmied weil=verworfen`), auf der Seite, die nicht vorn war
 *   07:29:41.6  Martin tippt auf d68c (dort war eben das Mikro aufgegangen) → vorn=d68c
 *   07:29:42.8  56cb ist fertig vertont, fragt „vorn?“ — jetzt d68c — und verwirft ebenfalls.
 * „Vorn“ wurde nur EINMAL gefragt, am Ende der Vertonung; wer dabei nicht vorn war, warf den Beitrag für immer weg. Wechselte
 * „vorn“ dazwischen, hatte die neue vordere Seite ihren eigenen Beitrag schon weggeworfen — niemand sprach.
 *
 * Jetzt: eine Stimme MIT Beitrags-Kennung (`kennung`, nur der Schmied) wird auf einer Seite, die nicht vorn ist, nicht
 * verworfen, sondern GEPARKT (`stimmeParken`, vertonter Ton bleibt liegen):
 *   - „vorn“: DIESE Seite wird vorn (der Server nennt sie, oder Martin bedient sie echt) → sie spricht ihn jetzt;
 *   - „anderswo“: eine andere Seite hat begonnen, ihn zu sprechen (Kennung im Kanal oder vom Server) → verwerfen, nie doppelt;
 *   - „frist“: nach `STIMME_PARK_MS` ohne beides → verwerfen wie bisher (`nicht-vorn`);
 *   - „abbruch“: die Schlange wurde abgebrochen (Stopp, Sprechtaste).
 * Jede Seite, die einen Beitrag zu sprechen BEGINNT, merkt und meldet seine Kennung (`stimmeBeginnt`) — auch eine, die
 * schon vorher gesprochen hat: eine Seite, die erst später parkt, findet sie in `gesprocheneKennungen`.
 * ⚠️ „vorn“ zählt hier nur, wenn DIESE Seite es selbst geworden ist (Server-Ereignis oder echte Bedienung seit dem Parken) —
 *    NICHT der Rückfall „niemand vorn/veraltet“: sonst sprächen beim Verschwinden des vorderen Geräts alle geparkten Seiten
 *    zugleich.
 * ⚠️ Grenzen: geht die Meldung „ich spreche ihn“ verloren (Kanal-Abriss, Server ohne Kennung), und wird die geparkte Seite
 *    danach vorn, spricht sie ihn ein zweites Mal. Und wird keine Seite vorn, die ihn hat (das vordere Gerät hat „Entwickler
 *    vorlesen“ aus oder bekam ihn nicht), bleibt er nach der Frist Text — wie vorher.
 */

/** So lange hält eine Seite, die nicht vorn ist, einen vertonten Beitrag zurück. */
export const STIMME_PARK_MS = 90_000;
/** So lange merkt sich eine Seite, welche Beiträge irgendwo zu sprechen begonnen haben. */
export const GESPROCHEN_MERKEN_MS = 10 * 60_000;

/** Was aus dem Parken wurde. */
export type ParkErgebnis = "vorn" | "anderswo" | "frist" | "abbruch";

/** Kennung → wann sie (hier oder anderswo) zu sprechen begann (Uhr dieser Seite). */
const gesprocheneKennungen = new Map<string, number>();

interface Geparkt {
  t: StimmenTeilnehmer;
  seit: number;
  ende: (e: ParkErgebnis) => void;
}
const geparkte = new Set<Geparkt>();

function merkeGesprochen(kennung: string, jetzt = Date.now()): void {
  gesprocheneKennungen.set(kennung, jetzt);
  for (const [k, um] of gesprocheneKennungen) if (jetzt - um > GESPROCHEN_MERKEN_MS) gesprocheneKennungen.delete(k);
  for (const p of [...geparkte]) if (p.t.kennung === kennung) p.ende("anderswo");
}

/** Hat dieser Beitrag (hier oder auf einer anderen Seite) schon zu sprechen begonnen? */
export function kennungGesprochen(kennung: string): boolean {
  return gesprocheneKennungen.has(kennung);
}

/** Wie viele Stimmen sind gerade geparkt? (Tests, Messung) */
export function stimmenGeparkt(): number {
  return geparkte.size;
}

/** Ist DIESE Seite selbst vorn geworden, seit `p` parkt (oben)? */
function geparktDarf(p: Geparkt, jetzt: number): boolean {
  // 28.10.2026: auch die Seite, deren Reihe läuft („Besitz“) — sie spricht ihren zurückgehaltenen Beitrag selbst.
  return vornUrteil(jetzt, p.t).spricht && (ichVorn || eigeneAktivSeit >= p.seit || (reihenfolgeAn() && eigenerBesitz !== null && laeuftNoch()));
}

function geparktePruefen(jetzt = Date.now()): void {
  for (const p of [...geparkte]) if (geparktDarf(p, jetzt)) p.ende("vorn");
}

/**
 * Diese Stimme durfte nicht beginnen (`nicht-vorn`): zurückhalten statt verwerfen (oben). Ohne Kennung: sofort `frist`
 * (wie bisher verwerfen). Wirft nie.
 */
export function stimmeParken(
  t: StimmenTeilnehmer,
  signal?: AbortSignal,
  maxMs: number = STIMME_PARK_MS,
  jetzt: () => number = Date.now,
): Promise<ParkErgebnis> {
  const kennung = t.kennung;
  if (signal?.aborted) return Promise.resolve("abbruch");
  if (!kennung || maxMs <= 0) return Promise.resolve("frist");
  if (gesprocheneKennungen.has(kennung)) return Promise.resolve("anderswo");
  return new Promise((ok) => {
    const ab = (): void => p.ende("abbruch");
    const frist = setTimeout(() => p.ende("frist"), maxMs);
    (frist as unknown as { unref?: () => void }).unref?.();
    const p: Geparkt = {
      t,
      seit: jetzt(),
      ende: (e) => {
        if (!geparkte.delete(p)) return;
        clearTimeout(frist);
        signal?.removeEventListener("abort", ab);
        ok(e);
      },
    };
    geparkte.add(p);
    signal?.addEventListener("abort", ab, { once: true });
  });
}

// ── Aufnahme über Geräte: Wach-Wort und Auto-Mikro warten ────────────────

/*
 * 17.10.2026 (Journal 08:16, belegt): Martin nahm am Mac per Taste auf (`via=knopf`); mitten im Satz hörte das Wach-Wort am
 * iPhone „Vespera“, öffnete dort eine zweite Aufnahme (`via=wachwort`) und holte sich „vorn“ — der Satz kam als zwei
 * Nachrichten von zwei Geräten an. Der Kanal oben („Aufnahme“) sieht nur Seiten DESSELBEN Browsers.
 *
 * Darum meldet eine Seite, die aufnimmt (jeder Startweg — es ist eine echte Aufnahme, kein Lauschen), das dem Server
 * (`STIMME_AUFNAHME_WEG`), erneuert es alle `AUFNAHME_KONTO_TAKT_MS` und meldet das Ende. Der Server reicht es an jede Seite
 * des Kontos (`aufnahmeVomKonto`). Dort gilt, solange die Meldung frisch ist (`AUFNAHME_KONTO_GILT_MS`, Uhr DIESER Seite):
 *  - das Auto-Mikro (nach Frage/Antwort) startet nicht, bei JEDER fremden Aufnahme; das Wach-Wort nur bei einer BEWUSST
 *    gestarteten (`AUFNAHME_BEWUSST`: Knopf, Aktionstaste, Pille, Freihand) — eine Aufnahme, die von selbst aufging
 *    (Auto-Mikro, Wach-Wort-Folge), sperrt das Wach-Wort am anderen Gerät nicht (Prüfung 17.10.: der sichere Standard).
 *    `zweitesGeraetSperrt`, mit der Journalzeile
 *    `[ptt] <via> übersprungen aufnahme-anderes-geraet vorn=<Gerät> andere=<Weg> seit=<s>s alter=<ms>ms`;
 *  - Knopf, Aktionstaste, Pille bleiben IMMER möglich — bewusste Bedienung hat Vorrang und übernimmt „vorn“.
 *
 * ⚠️ Die Meldung hält den Start nie auf: sie geht ab, während das Mikro anläuft (kein Warten, kein Netz nötig). Ein Gerät
 *    allein: nur diese eine Meldung je Aufnahme und ihr Takt — keine Abfrage, keine Wartezeit.
 * ⚠️ Ein abgestürztes Gerät sperrt höchstens `AUFNAHME_KONTO_GILT_MS` ab seiner letzten Meldung (kein Takt mehr; beim
 *    Nachreichen nennt der Server das Alter, `alterMs`). Jede Aufnahme trägt eine Laufnummer (`lauf`): ein Beginn, der
 *    sein eigenes Ende überholt, wird am Server verworfen.
 * ⚠️ Kommt das Ereignis nicht an (Kanal zu, Telefon im Hintergrund ohne Verbindung), sperrt nichts — Rückfall wie vorher.
 * ⚠️ Schalter am Server: `WACHWORT_ZWEITES_GERAET_SPERRE=0` — die Antwort trägt `aus: true`, die Seite meldet nichts mehr.
 */

/** Die Wege, die eine Aufnahme auf einem anderen Gerät sperrt — niemand hat hier gedrückt. */
const SPERRT_BEI_FREMDER_AUFNAHME: ReadonlySet<string> = new Set(["wachwort", "nach-frage", "nach-antwort"]);

/** Der Server sagt: die Sperre ist aus — dann meldet diese Seite nichts mehr. */
let kontoAufnahmeAus = false;
/** Was diese Seite zuletzt als laufend gemeldet hat (Weg, Zeit, Lauf) — `null`: nichts offen. */
let kontoAufnahmeGemeldet: { via: string; um: number; lauf: number } | null = null;
/** Laufnummer der Aufnahmen dieser Seite — jede Aufnahme eine neue (gegen Beginn/Ende, die einander überholen). */
let kontoAufnahmeLauf = 0;
/** Andere Geräte, die laut Server aufnehmen: Kennung → Weg, erste Ankunft, gültig bis (Uhr dieser Seite). */
const fremdeGeraeteAufnahmen = new Map<string, { via: string; seit: number; zuletzt: number; bis: number }>();

/** Meldet Beginn/Erneuerung (`laeuft`) oder Ende dem Server, mit der Laufnummer. Wirft nie, hält nichts auf. */
function kontoAufnahmeMelden(laeuft: boolean, via: string, lauf: number, jetzt = Date.now()): void {
  if (laeuft) kontoAufnahmeGemeldet = { via, um: jetzt, lauf };
  else kontoAufnahmeGemeldet = null;
  if (kontoAufnahmeAus || typeof fetch !== "function") return;
  try {
    void fetch(STIMME_AUFNAHME_WEG, {
      method: "POST",
      credentials: "include",
      headers: { [GERAET_KOPF]: EIGENES_GERAET, "Content-Type": "application/json" },
      body: JSON.stringify({ laeuft, via, lauf }),
      keepalive: true,
    })
      .then(async (r) => {
        if (!r.ok) return;
        const j = (await r.json().catch(() => null)) as { aus?: unknown } | null;
        if (j?.aus === true) kontoAufnahmeAus = true;
      })
      .catch(() => undefined);
  } catch {
    /* ohne Netz: die anderen Geräte sperren eben nicht */
  }
}

/** Im Takt der Aufnahme: alle `AUFNAHME_KONTO_TAKT_MS` erneuern, damit die Meldung nicht verfällt. */
function kontoAufnahmeErneuern(via: string, jetzt = Date.now()): void {
  const g = kontoAufnahmeGemeldet;
  if (!g || jetzt - g.um < AUFNAHME_KONTO_TAKT_MS) return;
  kontoAufnahmeMelden(true, via, g.lauf, jetzt);
}

/** Die Aufnahme dieser Seite ist zu Ende (gesendet, verworfen, gewichen, Seite zu): sofort freigeben. */
function kontoAufnahmeEnde(): void {
  const g = kontoAufnahmeGemeldet;
  if (!g) return;
  kontoAufnahmeMelden(false, g.via, g.lauf);
}

/** Der Server meldet: ein Gerät des Kontos nimmt auf (oder nicht mehr). Aus dem Kanal (chat-stream.ts `onStimmeAufnahme`). */
export function aufnahmeVomKonto(e: AufnahmeEreignis, jetzt = Date.now()): void {
  if (e.geraet === EIGENES_GERAET) return;
  if (!e.aufnahme) {
    fremdeGeraeteAufnahmen.delete(e.geraet);
    // Prüfbefund M2: ein Beitrag, den der Besitz während dieser Aufnahme zurückhielt, wird jetzt erneut geprüft.
    geparktePruefen(jetzt);
    return;
  }
  // Nachgereicht (neu verbunden): die Meldung ist schon `alterMs` alt — die Frist zählt ab ihr, nicht ab jetzt.
  const alter = Math.min(Math.max(0, e.alterMs ?? 0), AUFNAHME_KONTO_GILT_MS);
  const zuletzt = jetzt - alter;
  const vorher = fremdeGeraeteAufnahmen.get(e.geraet);
  const seit = vorher && vorher.bis > jetzt ? vorher.seit : zuletzt;
  fremdeGeraeteAufnahmen.set(e.geraet, { via: e.via, seit, zuletzt, bis: zuletzt + AUFNAHME_KONTO_GILT_MS });
}

/**
 * Nimmt laut Server gerade ein ANDERES Gerät auf? Mit Kennung, Weg, Dauer und Alter der letzten Meldung. `passt`: nur
 * Aufnahmen mit diesem Startweg zählen (Vorgabe: jede).
 */
export function fremdesGeraetNimmtAuf(
  jetzt = Date.now(),
  passt: (via: string) => boolean = () => true,
): { geraet: string; via: string; seitMs: number; alterMs: number } | null {
  for (const [geraet, a] of fremdeGeraeteAufnahmen) {
    if (a.bis <= jetzt) {
      fremdeGeraeteAufnahmen.delete(geraet);
      continue;
    }
    if (!passt(a.via)) continue;
    return { geraet, via: a.via, seitMs: Math.max(0, jetzt - a.seit), alterMs: Math.max(0, jetzt - a.zuletzt) };
  }
  return null;
}

/**
 * Bleibt dieser Start zu, weil ein anderes Gerät aufnimmt? Zurück kommt die Journalzeile (dann NICHT starten) — oder
 * `null`: starten wie bisher. Nur Wach-Wort und Auto-Mikro; jeder andere Weg (Knopf, Aktionstaste, Pille …) nie.
 * Das Wach-Wort sperrt nur eine BEWUSST gestartete fremde Aufnahme (`AUFNAHME_BEWUSST`), das Auto-Mikro jede.
 */
export function zweitesGeraetSperrt(via: string, jetzt = Date.now()): string | null {
  if (!SPERRT_BEI_FREMDER_AUFNAHME.has(via)) return null;
  const f = fremdesGeraetNimmtAuf(jetzt, via === "wachwort" ? (v) => AUFNAHME_BEWUSST.includes(v) : undefined);
  if (!f) return null;
  return (
    `[ptt] ${via} übersprungen aufnahme-anderes-geraet vorn=${kurzKennung(f.geraet)} andere=${f.via} ` +
    `seit=${Math.round(f.seitMs / 1000)}s alter=${Math.round(f.alterMs)}ms`
  );
}

// ── Fertig: nach Martins „Fertig“ kommt nichts nach ─────────────────────

/*
 * 17.10.2026 (Martin, 09:45): „Fertig“ an der Wiedergabe-Pille erklärt die Antwort für abgeschlossen. Auf DIESER Seite
 * stoppt app.ts alles (stopCurrentTts, vorbereitete Beiträge des Schmieds). Hier endet, was über die Seiten hinweg
 * zurückgehalten wird: geparkte Beiträge (nicht vorn, bis `STIMME_PARK_MS`), wartende Stimmen (Schlange), sprechende.
 * Andere Seiten desselben Browsers erfahren es über den Kanal, andere Geräte über den Server (`STIMME_FERTIG_WEG`) — dort
 * ruft `beiFertigAnderswo` denselben Weg in app.ts auf (ohne weiterzumelden). Ohne Rückruf (Tests): nur `stimmenFertig`.
 * ⚠️ Grenze: ein Gerät, dessen Kanal gerade neu verbindet (Hintergrund, Netzwechsel), verpasst das Ereignis — ein dort
 *    geparkter Beitrag verfällt dann erst mit seiner Frist (oder spricht, wenn Martin GENAU dieses Gerät bedient).
 */
let fertigAnderswo: ((von: string) => void) | null = null;

/** app.ts: was eine Seite tut, wenn „Fertig“ auf einer ANDEREN Seite oder einem anderen Gerät gedrückt wurde. */
export function beiFertigAnderswo(rueckruf: ((von: string) => void) | null): void {
  fertigAnderswo = rueckruf;
}

/**
 * „Fertig“: geparkte und wartende Stimmen dieser Seite werden verworfen, sprechende enden (`abbruch:fertig`). `weiter`:
 * den anderen Seiten und Geräten melden (nur, wo Martin gedrückt hat). Rückgabe: wie viele geparkt oder wartend waren.
 * Wirft nie.
 */
export function stimmenFertig(weiter: boolean, grund = "fertig"): number {
  const verworfen = geparkte.size + wartende.size;
  for (const p of [...geparkte]) {
    try {
      p.t.cancel(grund);
    } catch {
      /* eine Schlange darf die andere nicht aufhalten */
    }
    p.ende("abbruch");
  }
  stimmenBeenden(grund);
  if (weiter) {
    sende({ t: "fertig", seite: EIGENES_GERAET });
    meldeKontoFertig();
  }
  return verworfen;
}

function meldeKontoFertig(): void {
  if (typeof fetch !== "function") return;
  try {
    void fetch(STIMME_FERTIG_WEG, { method: "POST", credentials: "include", headers: { [GERAET_KOPF]: EIGENES_GERAET } }).catch(
      () => undefined,
    );
  } catch {
    /* ohne Netz bleibt es bei dieser Seite und dem Kanal */
  }
}

function fertigVonAnderswo(von: string): void {
  if (!fertigAnderswo) {
    stimmenFertig(false, "fertig-anderswo");
    return;
  }
  try {
    fertigAnderswo(von);
  } catch {
    /* der Rückruf darf den Eingang nicht stören */
  }
}

/** Der Server meldet „Fertig“ einer Seite des Kontos (chat-stream.ts `onStimmeFertig`). Das eigene und das einer Seite dieses Browsers (die kam über den Kanal) zählen nicht. */
export function fertigVomKonto(e: FertigEreignis): void {
  if (e.geraet === EIGENES_GERAET || kanalSeiten.has(e.geraet)) return;
  fertigVonAnderswo(e.geraet);
}

// ── Eingang ──────────────────────────────────────────────────────────────

function nimmAn(n: Nachricht): void {
  if (!n || typeof n !== "object" || n.seite === EIGENES_GERAET) return;
  if (typeof n.seite === "string") kanalSeiten.add(n.seite);
  if (n.t === "fertig") {
    fertigVonAnderswo(n.seite);
    return;
  }
  if (n.t === "stimme") {
    if (n.kennung) {
      merkeGesprochen(n.kennung);
      merkeFremd(n.kennung);
    }
    hoechsteFremdeStimme = Math.max(hoechsteFremdeStimme, n.ts);
    if (stimmen.size === 0) return;
    const gleich = !!n.kennung && n.kennung === meineKennung;
    const vornUrteilGleich = gleich ? vornVorrangGleicherBeitrag(n.seite) : null;
    const fremdGewinnt =
      vornUrteilGleich ?? gewinntFremdeStimme({ ts: meineStimmeTs, seite: EIGENES_GERAET }, { ts: n.ts, seite: n.seite }, gleich);
    if (!fremdGewinnt) return;
    stimmenBeenden("andere-seite");
    return;
  }
  if (n.t !== "aufnahme") return;
  if (!n.laeuft) {
    fremdeAufnahmen.delete(n.seite);
    return;
  }
  const jetzt = Date.now();
  // Frisch = diese Aufnahme kennt diese Seite noch nicht (der Takt erneuert dieselbe `seit`).
  const frisch = fremdeAufnahmen.get(n.seite)?.seit !== n.seit;
  fremdeAufnahmen.set(n.seite, { via: n.via, seit: n.seit, bis: jetzt + GILT_MS });
  /*
   * ⚠️ Eine andere Seite nimmt auf: die eigenen Stimmen enden. Auf EINER Seite
   *    stoppt jeder Druck die Stimme (stopCurrentTts „ptt"); über Seiten hinweg
   *    fehlte dieses Gegenstück — diese Seite sprach weiter, in das Mikrofon der
   *    anderen. Nur bei einer FRISCHEN Anmeldung: der Takt (jede Sekunde dieselbe
   *    Aufnahme) und eine Stimme, die danach beginnt, sind Sache von `stimmeBeginnt`.
   */
  if (frisch) stimmenBeenden("andere-seite");
  const e = eigeneAufnahme;
  if (
    e &&
    jetzt - e.seit <= RENNEN_MS &&
    gewinntFremdeAufnahme({ seit: e.seit, seite: EIGENES_GERAET }, { seit: n.seit, seite: n.seite })
  ) {
    // Die eigene Anmeldung zurücknehmen, dann weichen — der Takt meldet es.
    eigeneAufnahme = null;
    sende({ t: "aufnahme", seite: EIGENES_GERAET, via: e.via, seit: e.seit, laeuft: false });
    kontoAufnahmeEnde();
    try {
      verloren?.({ seite: n.seite, via: n.via, seitMs: Math.max(0, jetzt - n.seit) });
    } catch {
      /* der Rückruf darf den Eingang nicht stören */
    }
  }
}
